import * as localizationMaps from '../../../localization/maps.js';
import i18n from '../../../localization/i18n';
import tokens from '../../../localization/tokens.js';
import * as Services from '../../../services/index.js';
import * as dates from '@simplybenefits/sb-dates';
const statusMap = {
  0: 'Pending',
  1: 'Active',
  2: 'Account Disabled',
  3: 'Parental Leave',
  4: 'Leave of Absence',
  5: 'Temporary Lay Off',
  6: 'Disability',
  7: 'Strike',
  8: 'Late Applicant',
  9: 'Terminated',
  10: 'Approved Leave',
  11: 'Survivor Benefits',
  12: 'Severance',
  'Pending': 0,
  'Active': 1,
  'Account Disabled': 2,
  'Parental Leave': 3,
  'Leave of Absence': 4,
  'Temporary Lay Off': 5,
  'Disability': 6,
  'Strike': 7,
  'Late Applicant': 8,
  'Terminated': 9,
  'Approved Leave': 10,
  'Survivor Benefits': 11,
  'Severance': 12
};

/**
 * Parses and formats the initial user data for editing or creation forms.
 * 
 * This function processes the given user data and formats it for use in forms. It adjusts the data based on whether the form
 * is in edit mode or create mode, formats dates, maps certain fields to human-readable formats, and provides default values
 * when necessary.
 * 
 * @param {Object} userData - The user data to be parsed and formatted.
 * @param {boolean} isEdit - Flag indicating if the form is in edit mode.
 * @param {Object} classData - The data containing class information.
 * @param {Object} divisions - The data containing division information.
 * @returns {Object} - The parsed and formatted user data ready for use in the form.
 */
const parseInitialUserData = (userData, isEdit, classData, divisions) => {
  const employeeLanguage = userData?.language === "fr" ? i18n.t(tokens.dropdowns.languages.french) : i18n.t(tokens.dropdowns.languages.english);
  const data = {
    firstname: userData && isEdit ? userData.firstname : '',
    lastname: userData && isEdit ? userData.lastname : '',
    status: userData && isEdit ? typeof userData.status === 'number' ? statusMap[Number(userData.status)] : userData.status : '',
    benefitsActive: userData && isEdit ? userData.benefitsActive : 0,
    email: userData && isEdit ? userData.email : '',
    birthdate: userData && isEdit ? Services.dates.formatIncomingDate(userData.birthdate) : '',
    phone: userData && isEdit ? userData.phone : '',
    maritlStat: userData && isEdit ? localizationMaps.fromEnglish('maritalStatuses', userData.maritlStat) : '',
    className: userData && isEdit ? classData[userData.classID]?.title : Object.values(classData)[0].title,
    divisionName: userData && isEdit ? divisions[userData.divisionID]?.title : Object.values(divisions)[0].title,
    hireDate: userData && isEdit ? Services.dates.formatIncomingDate(userData.hireDate) : '',
    gender: userData && isEdit ? localizationMaps.fromEnglish("genders", userData.gender) : '',
    title: userData && isEdit ? userData.title : '',
    coverageType: userData && isEdit ? localizationMaps.fromEnglish("coverageTypes", userData.coverageType) : '',
    province: userData && isEdit ? localizationMaps.fromEnglish("provinces", userData.province) : '',
    addressLineOne: userData && isEdit ? userData.addressLineOne : '',
    addressLineTwo: userData && isEdit ? userData.addressLineTwo : '',
    city: userData && isEdit ? userData.city : '',
    postalCode: userData && isEdit ? userData.postalCode : '',
    earningsType: userData && isEdit ? localizationMaps.fromEnglish("earningsTypes", userData.earningsType) : '',
    salary: userData && isEdit ? userData.salary : '',
    hourlyWage: userData && isEdit ? userData.hourlyWage : '',
    hoursPerWeek: userData && isEdit ? userData.hoursPerWeek : '',
    language: employeeLanguage,
    payrollID: userData && isEdit ? userData.payrollID : '',
    waiveEligibilityPeriod: userData && isEdit && userData.waiveEligibilityPeriod ? userData.waiveEligibilityPeriod : false,
    studentID: userData && isEdit ? userData.studentID : '',
    studyStartDate: userData && isEdit && userData.studyStartDate ? Services.dates.formatIncomingDate(userData.studyStartDate) : '',
    degree: userData && isEdit ? userData.degree : ''
  };
  return data;
};

/**
 * Checks if there are any changes in the user's personal information compared to the initial data.
 * 
 * This function compares the current user data with the initial data to determine if there are any
 * differences in the following fields: firstname, lastname, email, birthdate, phone, and marital status.
 * It returns true if any of these fields have changed, otherwise false.
 * 
 * @param {Object} userData - The current user data.
 * @param {Object} initialData - The initial user data.
 * @returns {boolean} - Returns true if there are changes in the personal information, false otherwise.
 */
const hasPersonalInfoChanges = (userData, initialData) => userData.firstname !== initialData.firstname || userData.lastname !== initialData.lastname || userData.email !== initialData.email || dates.getUTC(userData.birthdate) !== initialData.birthdate || userData.phone !== initialData.phone || localizationMaps.toEnglish('maritalStatuses', userData.maritlStat) !== initialData.maritlStat;

/**
 * Returns the classID for the given class title.
 * 
 * @param {*} classData - Object containing class data
 * @param {*} classTitle - Title of the class
 * @returns {*} classID
 */
const returnClassID = (classData, classTitle) => {
  const classID = Object.values(classData).find(e => e.title === classTitle)?.classID;
  return classID;
};

/**
 * Returns the divisionID for the given division title.
 * 
 * @param {*} divisions - Object containing division data
 * @param {*} divisionTitle - Title of the division
 * @returns {*} divisionID
 */
const returnDivisionID = (divisions, divisionName) => {
  const divisionID = Object.values(divisions).find(e => e.title === divisionName)?.divisionID;
  return divisionID;
};

/**
 * Checks if there are any changes in the user's additional details compared to the initial data.
 * 
 * This function compares the current user data with the initial data to determine if there are any
 * differences in the following fields: divisionName, title, province, earningsType, salary, hourlyWage,
 * hoursPerWeek, language, payrollID, and waiveEligibilityPeriod, and the equivalent student group fields. It returns an object containing the fields
 * that have changed along with their new values. If no changes are found, it returns an empty object.
 * 
 * @param {Object} userData - The current user data.
 * @param {Object} initialData - The initial user data.
 * @returns {Object} - An object containing the changed fields and their new values, or an empty object if no changes are found.
 */
const returnRegularInfoChanges = (userData, initialData, divisions, isStudentGroup = false) => {
  const changes = {};
  if (returnDivisionID(divisions, userData.divisionName) !== initialData.divisionID) {
    changes.divisionID = returnDivisionID(divisions, userData.divisionName);
  }
  if (userData.title !== initialData.title) {
    changes.title = userData.title;
  }
  if (localizationMaps.toEnglish('provinces', userData.province) !== initialData.province) {
    changes.province = localizationMaps.toEnglish('provinces', userData.province);
  }
  if (localizationMaps.toEnglish('genders', userData.gender) !== initialData.gender) {
    changes.gender = localizationMaps.toEnglish('genders', userData.gender);
  }

  // student groups do not use earnings type, so there is no need to compare this data for a a student group
  if (!isStudentGroup && localizationMaps.toEnglish('earningsTypes', userData.earningsType) !== initialData.earningsType) {
    changes.earningsType = localizationMaps.toEnglish('earningsTypes', userData.earningsType);
  }

  // student groups do not use salary, so there is no need to compare this data for a a student group
  if (!isStudentGroup && Number(userData.salary) !== initialData.salary) {
    changes.salary = Number(userData.salary);
  }

  // student groups do not use hourly wage, so there is no need to compare this data for a a student group
  if (!isStudentGroup && userData.hourlyWage !== initialData.hourlyWage) {
    changes.hourlyWage = userData.hourlyWage;
  }

  // student groups do not use hours per week, so there is no need to compare this data for a a student group
  if (!isStudentGroup && Number(userData.hoursPerWeek) !== initialData.hoursPerWeek) {
    changes.hoursPerWeek = Number(userData.hoursPerWeek);
  }
  if (localizationMaps.toEnglish('languages', userData.language) === 'English' && initialData.language === 'fr') {
    changes.language = localizationMaps.toEnglish('languages', userData.language);
  }
  if (localizationMaps.toEnglish('languages', userData.language) !== 'English' && initialData.language === 'en') {
    changes.language = localizationMaps.toEnglish('languages', userData.language);
  }

  // student groups do not use payroll id, so there is no need to compare this data for a a student group
  if (!isStudentGroup && userData.payrollID !== initialData.payrollID) {
    changes.payrollID = userData.payrollID;
  }
  if (userData.addressLineOne !== initialData.addressLineOne) {
    changes.addressLineOne = userData.addressLineOne;
  }
  if (userData.addressLineTwo !== initialData.addressLineTwo) {
    changes.addressLineTwo = userData.addressLineTwo;
  }
  if (userData.city !== initialData.city) {
    changes.city = userData.city;
  }
  if (userData.postalCode !== initialData.postalCode) {
    changes.postalCode = userData.postalCode;
  }
  if (isStudentGroup && dates.getUTC(userData.studyStartDate) !== initialData.studyStartDate) {
    changes.studyStartDate = dates.getUTC(userData.studyStartDate);
  }
  if (isStudentGroup && userData.degree !== initialData.degree) {
    changes.degree = userData.degree;
  }
  return Object.keys(changes).length ? changes : false;
};

/**
 * Checks if there are any changes in the user's personal information compared to the initial data.
 * 
 * This function compares the current user data with the initial data to determine if there are any
 * differences in the following fields: firstname, lastname, email, birthdate, phone, and marital status.
 * It returns an object containing the fields that have changed along with their new values. If no changes
 * are found, it returns an empty object.
 * 
 * @param {Object} userData - The current user data.
 * @param {Object} initialData - The initial user data.
 * @returns {Object} - An object containing the changed fields and their new values, or an empty object if no changes are found.
 */
const returnPersonalInfoChanges = (userData, initialData) => {
  const changes = {};
  if (userData.firstname !== initialData.firstname) {
    changes.firstname = userData.firstname;
  }
  if (userData.lastname !== initialData.lastname) {
    changes.lastname = userData.lastname;
  }
  if (userData.email !== initialData.email) {
    changes.email = userData.email;
  }
  if (dates.getUTC(userData.birthdate) !== initialData.birthdate) {
    changes.birthdate = dates.getUTC(userData.birthdate);
  }
  if (userData.phone !== initialData.phone) {
    changes.phone = userData.phone;
  }
  if (localizationMaps.toEnglish('maritalStatuses', userData.maritlStat) !== initialData.maritlStat) {
    changes.maritlStat = localizationMaps.toEnglish('maritalStatuses', userData.maritlStat);
  }
  return Object.keys(changes).length ? changes : false;
};

/**
 * Checks if there are any changes in the user's employment details compared to the initial data.
 * 
 * This function compares the current user data with the initial data to determine if there are any
 * differences in the following fields: hireDate and className. It returns an object containing the
 * fields that have changed along with their new values. If no changes are found, it returns an empty object.
 * 
 * @param {Object} userData - The current user data.
 * @param {Object} initialData - The initial user data.
 * @returns {Object} - An object containing the changed fields and their new values, or an empty object if no changes are found.
 */
const returnEmploymentInfoChanges = (userData, initialData, classData, isStudentGroup = false) => {
  const changes = {};

  // student groups do not use hire date, so there is no need to compare this data for a a student group
  if (!isStudentGroup && dates.getUTC(userData.hireDate) !== initialData.hireDate) {
    changes.hireDate = dates.getUTC(userData.hireDate);
  }
  if (returnClassID(classData, userData.className) !== initialData.classID) {
    changes.classID = returnClassID(classData, userData.className);
  }
  if (Boolean(userData.waiveEligibilityPeriod) !== Boolean(initialData.waiveEligibilityPeriod)) {
    changes.waiveEligibilityPeriod = userData.waiveEligibilityPeriod;
  }
  return Object.keys(changes).length ? changes : false;
};

/**
 * Initial error state matching the deconstructed error state fields
 */
const initialErrorState = {
  firstnameError: false,
  lastnameError: false,
  emailError: false,
  birthdateError: false,
  phoneError: false,
  maritalStatusError: false,
  classNameError: false,
  divisionNameError: false,
  hireDateError: false,
  genderError: false,
  titleError: false,
  coverageTypeError: false,
  provinceError: false,
  earningsTypeError: false,
  salaryError: false,
  hourlyWageError: false,
  hoursPerWeekError: false,
  languageError: false,
  payrollIDError: false,
  waiveEligibilityPeriodError: false,
  employeeDetailsPersonalDetailsError: false,
  studentIDError: false,
  studyStartDateError: false,
  degreeError: false
};

/**
 * 
 * @param {*} language 
 * @returns 
 */
const getLanguage = language => {
  if (language === 'en' || language === 'fr') {
    return language;
  }
  let parsedLanguage = 'en';
  parsedLanguage = language === 'French' || language === 'Français' ? 'fr' : 'en';
  return parsedLanguage;
};

/**
 * Parses and prepares user data for an update operation from the ADMIN site, merging updated fields from userData
 * with initialData where applicable, ensuring that the correct data is sent.
 * 
 * @param {Object} userData - Updated user data containing fields to update
 * @param {Object} initialData - Initial user data serving as a baseline
 * @returns {Object} Updated user data object with merged fields ready for update
 */
const parseUserDataForUpdate = (userData, initialData, divisions, isStudent = false) => {
  const addressData = {
    addressLineOne: userData?.addressLineOne?.trim() || initialData.addressLineOne,
    addressLineTwo: userData?.addressLineTwo?.trim() || initialData.addressLineTwo || null,
    postalCode: userData?.postalCode?.trim() || initialData.postalCode,
    province: localizationMaps.toEnglish("provinces", userData?.province || initialData.province),
    city: userData?.city?.trim() || initialData.city
  };

  // add this data if the new employee IS PART of a student account
  const studentData = {
    phone: userData?.phone || initialData.phone,
    maritlStat: localizationMaps.toEnglish('maritalStatuses', userData?.maritlStat || initialData.maritlStat),
    studentID: initialData.studentID,
    // we can never edit the student id as this is the cert number, and is synced with telus
    studyStartDate: userData?.studyStartDate || initialData.studyStartDate,
    // the parsing to UTC is done in the function that checks if the study start date has changed
    degree: userData?.degree?.trim() || null,
    ...addressData
  };

  /*
   * add this data if the new employee is not part of a student account
   * the initial data is always used to update the employee data for certain fields when an admin is making the changes, because change requests are required for those fields
   */
  const employmentData = {
    earningsType: userData?.earningsType || initialData.earningsType,
    salary: userData?.salary || initialData.salary,
    hourlyWage: userData?.hourlyWage || initialData.hourlyWage,
    hoursPerWeek: userData?.hoursPerWeek || initialData.hoursPerWeek,
    title: userData?.title?.trim() || initialData.title,
    payrollID: userData?.payrollID || initialData.payrollID || null,
    // payroll id can be null
    hireDate: initialData.hireDate
  };
  const data = {
    email: initialData.email?.trim(),
    classID: initialData.classID,
    divisionID: userData?.divisionID || returnDivisionID(divisions, userData.divisionName) || initialData.divisionID,
    firstname: initialData.firstname?.trim(),
    lastname: initialData.lastname?.trim(),
    birthdate: initialData.birthdate,
    language: getLanguage(userData?.language) || getLanguage(initialData.language),
    gender: userData?.gender || initialData.gender,
    coverageType: localizationMaps.toEnglish('coverageTypes', userData.coverageType) || initialData.coverageType || 'Solo',
    province: localizationMaps.toEnglish("provinces", userData?.province) || initialData.province,
    ...addressData,
    ...(isStudent ? {
      ...studentData
    } : {
      ...employmentData
    })
  };
  return data;
};

/**
 * 
 * @param {*} userData 
 * @param {*} classData 
 * @param {*} divisions 
 * @param {*} isStudent 
 * @param {*} isMasterSite 
 * @param {*} isEdit 
 * @param {*} isProfile 
 * @returns all parsed user data, for use in adding and editing users from the advisor and master sites
 */
const parseUserDataForAddUpdate = (userData, classData, divisions, isStudent = false, isMasterSite = false, isEdit = false, isProfile = false // this is used when called from the master site from the member profile, which requires sending some extra data
) => {
  const addressData = {
    addressLineOne: userData.addressLineOne ? userData.addressLineOne.trim() : null,
    addressLineTwo: userData?.addressLineTwo?.trim() || null,
    city: userData?.city?.trim(),
    postalCode: userData?.postalCode,
    province: localizationMaps.toEnglish("provinces", userData?.province)
  };

  // send this data if the new employee IS PART of a student account
  const studentData = {
    studentID: userData?.studentID,
    studyStartDate: dates.getUTC(userData.studyStartDate),
    degree: userData?.degree?.trim() || null
  };

  // send this data if the new employee is NOT part of a student account
  const employmentData = {
    earningsType: localizationMaps.toEnglish('earningsTypes', userData?.earningsType),
    salary: Number(userData?.salary),
    hourlyWage: Number(userData?.hourlyWage),
    hoursPerWeek: Number(userData?.hoursPerWeek),
    title: userData?.title?.trim(),
    payrollID: userData?.payrollID?.trim(),
    hireDate: dates.getUTC(userData.hireDate)
  };

  // we allow changing the users status and benefitsActive, and expected leave return date values if we are in the master site master profile view
  const profileUpdateData = {
    expectedLeaveReturnDate: userData?.expectedLeaveReturnData && userData?.expectedLeaveReturnDate?.length > 0 ? dates.getUTC(userData?.expectedLeaveReturnDate) : null,
    status: typeof userData?.status !== 'number' ? statusMap[userData?.status] : userData?.status,
    benefitsActive: userData?.benefitsActive
  };
  const data = {
    email: userData.email?.trim(),
    classID: returnClassID(classData, userData.className),
    divisionID: returnDivisionID(divisions, userData.divisionName),
    firstname: userData.firstname?.trim(),
    lastname: userData.lastname?.trim(),
    birthdate: dates.getUTC(userData.birthdate),
    province: localizationMaps.toEnglish('provinces', userData.province),
    language: getLanguage(userData.language),
    gender: localizationMaps.toEnglish("genders", userData.gender),
    coverageType: localizationMaps.toEnglish('coverageTypes', userData.coverageType),
    phone: isEdit || isStudent ? userData?.phone?.trim() : null,
    // we do not show this field or marital stat when adding a new user to a non student account, as this data gets set by the member in enrollment, so only send it if we are editing the member or adding a member to a student group
    maritlStat: isEdit || isStudent ? localizationMaps.toEnglish('maritalStatuses', userData?.maritlStat) : null,
    effectiveDate: dates.getUTC(),
    ...(!isMasterSite && isEdit ? {} : {
      waiveEligibilityPeriod: Boolean(userData.waiveEligibilityPeriod)
    }),
    // we do not send the waiveEligiblity period for an edit from the advisor site, only for the master
    ...(isEdit || isStudent ? {
      ...addressData
    } : {}),
    // send the mailing address if we are editing a user, or if the user is a student
    ...(isStudent ? {
      ...studentData
    } : {
      ...employmentData
    }),
    ...(isProfile ? {
      ...profileUpdateData
    } : {})
  };
  return data;
};

/**
 * 
 * @param {*} isStudentGroup 
 * @param {*} isTerminated 
 * @returns 
 */
const returnStatusOptions = (isStudentGroup, isTerminated) => {
  const statusOptions = [{
    key: '1',
    value: 'Active'
  }, {
    key: '10',
    value: 'Approved Leave'
  }, {
    key: '2',
    value: 'Account Disabled'
  }, {
    key: '6',
    value: 'Disability'
  }, {
    key: '4',
    value: 'Leave of Absence'
  }, {
    key: '3',
    value: 'Parental Leave'
  }, {
    key: '0',
    value: 'Pending'
  }, {
    key: '11',
    value: 'Survivor Benefits'
  }];
  if (!isStudentGroup) {
    statusOptions.push({
      key: '8',
      value: 'Late Applicant'
    });
    statusOptions.push({
      key: '7',
      value: 'Strike'
    });
    statusOptions.push({
      key: '5',
      value: 'Temporary Lay Off'
    });
    statusOptions.push({
      key: '12',
      value: 'Severance'
    });
  }
  if (isTerminated) {
    statusOptions.push({
      key: '9',
      value: 'Terminated'
    });
  }

  // Sort status options alphabetically by value
  statusOptions.sort((a, b) => a.value.localeCompare(b.value));
  return statusOptions;
};
export { parseInitialUserData, hasPersonalInfoChanges, returnRegularInfoChanges, returnPersonalInfoChanges, returnEmploymentInfoChanges, initialErrorState, parseUserDataForUpdate, parseUserDataForAddUpdate, returnClassID, returnStatusOptions, statusMap };